import React, { useState, useRef, useCallback } from "react";
import classNames from "classnames";

const useLongPress = (
  onLongPress,
  onClick,
  onClear,
  { shouldPreventDefault = true, delay = 300 } = {}
) => {
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const timeout = useRef();
  const target = useRef();

  const start = useCallback(
    (event) => {
      if (shouldPreventDefault && event.target) {
        event.target.addEventListener("touchend", preventDefault, {
          passive: false,
        });
        target.current = event.target;
      }
      // @ts-ignore
      timeout.current = setTimeout(() => {
        onLongPress(event);
        setLongPressTriggered(true);
      }, delay);
    },
    [onLongPress, delay, shouldPreventDefault]
  );

  const clear = useCallback(
    (event, shouldTriggerClick = true) => {
      timeout.current && clearTimeout(timeout.current);
      shouldTriggerClick && !longPressTriggered && onClick();
      setLongPressTriggered(false);
      if (shouldPreventDefault && target.current) {
        onClear();

        // @ts-ignore
        target.current.removeEventListener("touchend", preventDefault);
      }
    },
    [shouldPreventDefault, onClick, longPressTriggered, onClear]
  );

  return {
    onMouseDown: (e) => start(e),
    onTouchStart: (e) => start(e),
    onMouseUp: (e) => clear(e),
    onMouseLeave: (e) => clear(e, false),
    onTouchEnd: (e) => clear(e),
  };
};

const isTouchEvent = (event) => {
  return "touches" in event;
};

const preventDefault = (event) => {
  if (!isTouchEvent(event)) return;

  if (event.touches.length < 2 && event.preventDefault) {
    event.preventDefault();
  }
};

interface ButtonHoldProps extends React.HTMLAttributes<HTMLButtonElement> {
  label?: string;
  labelStarting?: string;
  labelHolding?: string;
  buttonClassName?: string;
  textClassName?: string;
}
const ButtonHold: React.FC<ButtonHoldProps> = ({
  children,
  label,
  labelStarting,
  labelHolding,
  buttonClassName,
  textClassName,
}) => {
  const [isHolding, setIsHolding] = useState(false);

  const [beganHolding, setBeganHolding] = useState(false);

  const onLongPress = () => {
    setIsHolding(true);
  };

  const onClick = () => {
    setBeganHolding(true);
  };

  const onClear = () => {
    setIsHolding(false);
    setBeganHolding(false);
  };

  const defaultOptions = {
    shouldPreventDefault: true,
    delay: 0,
  };
  const longPressEvent = useLongPress(
    onLongPress,
    onClick,
    onClear,
    defaultOptions
  );

  return (
    <button
      className={classNames(
        "py-[14px]  w-[100%] border border-auctionStroke",
        buttonClassName,
        isHolding ? "bg-black" : ""
      )}
      {...longPressEvent}
    >
      <p
        className={classNames(
          "text-[23px]",
          textClassName,
          isHolding ? "text-white" : ""
        )}
      >
        {!isHolding ? label : beganHolding ? labelStarting : labelHolding}
      </p>
      {children}
    </button>
  );
};

export default ButtonHold;

